import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { predefinedDateRanges } from './DateRanges.utils';
import { Dayjs } from 'dayjs';

interface DateRangeState {
  dateRange: [Dayjs, Dayjs];
}

const initialState: DateRangeState = {
  dateRange: predefinedDateRanges.find(dr => dr.isDefault)?.value ?? predefinedDateRanges[0].value,
} as DateRangeState;

const DateRangeContext = createContext<
  | {
      dateRange: [Dayjs, Dayjs];
      setDateRange: (dateRange: [Dayjs, Dayjs]) => void;
    }
  | undefined
>(undefined);

export const DateRangeProvider = ({ children }: { children: React.ReactNode }) => {
  const [dateRange, setDateRange] = useState(initialState.dateRange);

  const handleDateRange = useCallback((dateRange: [Dayjs, Dayjs]) => {
    const validDateRange = dateRange.filter(dr => dr !== null);

    if (validDateRange.length === 1) {
      setDateRange([validDateRange[0], validDateRange[0]]);
    } else {
      setDateRange(dateRange);
    }
  }, []);

  const values = useMemo(() => {
    return {
      dateRange,
      setDateRange: handleDateRange,
    };
  }, [dateRange, handleDateRange]);

  return <DateRangeContext.Provider value={values}>{children}</DateRangeContext.Provider>;
};

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return { dateRange: context.dateRange, setDateRange: context.setDateRange };
};
